import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { useRefreshTokenOnMount } from '@user/store/authorization/hooks'
import { getStyles } from './useStyles'
import { TopToolbar } from '../TopToolbar'
import { SideNav } from './SideNav'
import AppRoutes from '../Routes'
import { useLoadFilterPreferencesOnMount } from '@user/store/userPreferences/hooks'
import { useLoadGeneralPreferencesOnMount } from '@user/store/userPreferences/userPreferencesGeneral/hooks'
import { useLoadCompanyIdentifierOnMount } from '@user/features/CompanyIdentifier/hooks'
import { useCheckSchedulingEligibilityOnMount } from '@user/store/scheduling'
import GlobalStyles from '@mui/material/GlobalStyles'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useBrowserHistory } from '@user/utils/useBrowserHistory'
import { useVisualViewportHeight } from '@user/utils/useVisualViewportHeight'
import Box from '@mui/material/Box'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'

const App: React.FC = () => {
  const location = useLocation()
  const currentUser = useSelector(selectCurrentUser)

  const accessDeniedRoute = location.pathname === '/access-denied'

  const styles = getStyles(accessDeniedRoute)

  const { loading: refreshingToken, authenticated } = useRefreshTokenOnMount()
  useBrowserHistory()
  useVisualViewportHeight()
  const featuresLoading = useCheckSchedulingEligibilityOnMount(authenticated)

  const { loading: filtersLoading } =
    useLoadFilterPreferencesOnMount(authenticated)
  const { loading: prefsLoading } =
    useLoadGeneralPreferencesOnMount(authenticated)
  useLoadCompanyIdentifierOnMount(authenticated)
  const tidalThemeActive = useTidalThemeActive()
  const loading =
    refreshingToken || featuresLoading || filtersLoading || prefsLoading

  const isGuestUser = currentUser?.roleName === guestRoleName

  return loading ? null : (
    <Box sx={styles.root}>
      {!tidalThemeActive && (
        <GlobalStyles
          styles={{
            ':root': {
              '--tidal-illustration-color-primary-dark': '#0E4686',
              '--tidal-illustration-color-primary-light': '#4383CC',
              '--tidal-illustration-color-secondary-dark': '#A1C1E6',
              '--tidal-illustration-color-secondary-main': '#D4E3F3',
            },
          }}
        />
      )}
      {authenticated && !accessDeniedRoute && <TopToolbar />}
      {authenticated && !accessDeniedRoute && <SideNav />}
      <Box sx={styles.content}>
        {authenticated && !accessDeniedRoute && (
          <Box sx={{ ...styles.toolbar }} />
        )}
        <AppRoutes authenticated={authenticated} isGuestUser={isGuestUser} />
      </Box>
    </Box>
  )
}
App.displayName = 'App'

export default App
